const VERSION = 1;
const MAX_AGE = 180 * 24 * 60 * 60 * 1000;
const memory = new Map();
let embeddedConsent = null;
export const CONSENT_EVENT = 'mib:practice-consent-updated';
export const consentKey = (config) => `mib_practice_consent_v1:${config.organization_id}`;
export const clearMemoryConsent = (config) => memory.delete(consentKey(config));

export function readConsent(config) {
  if (!config?.organization_id || !config?.revision) return null;
  if (window.parent !== window) {
    return embeddedConsent?.organization_id === config.organization_id
      && embeddedConsent.revision === config.revision ? embeddedConsent.choice : null;
  }
  const key = consentKey(config);
  let value;
  try { value = memory.has(key) ? memory.get(key) : JSON.parse(window.localStorage.getItem(key)); } catch { value = memory.get(key); }
  if (!value || value.version !== VERSION || value.revision !== config.revision
    || typeof value.analytics !== 'boolean' || typeof value.marketing !== 'boolean') return null;
  const age = Date.now() - Date.parse(value.saved_at);
  return Number.isFinite(age) && age >= 0 && age < MAX_AGE ? value : null;
}

export function saveConsent(config, choices) {
  // A website choice must never become a durable grant for the shared booking host.
  if (window.parent !== window) return null;
  const value = {
    version: VERSION, revision: config.revision, source: 'mib_banner',
    analytics: choices.analytics === true, marketing: choices.marketing === true,
    saved_at: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(consentKey(config), JSON.stringify(value));
    memory.delete(consentKey(config));
  } catch { memory.set(consentKey(config), value); }
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: { organization_id: config.organization_id } }));
  return value;
}

/** Bind a website's live choice to this practice, document and parent window only. */
export function connectEmbeddedConsent(config) {
  if (window.parent === window) return () => {};
  embeddedConsent = null;
  let origin;
  let channel;
  try {
    const query = new URLSearchParams(window.location.search);
    if (query.get('consent_manager') !== 'complianz') return () => {};
    const privacy = new URL(config.privacy_policy_url);
    const local = ['localhost', '127.0.0.1', '[::1]'].includes(privacy.hostname);
    if (privacy.protocol !== 'https:' && !(local && privacy.protocol === 'http:')) return () => {};
    origin = query.get('parent_origin');
    channel = query.get('consent_channel');
    if (origin !== privacy.origin || !/^[a-zA-Z0-9-]{20,80}$/.test(channel || '')) return () => {};
  } catch { return () => {}; }
  const connection = { organization_id: config.organization_id, revision: config.revision, choice: null };
  embeddedConsent = connection;
  const changed = () => window.dispatchEvent(new CustomEvent(CONSENT_EVENT, {
    detail: { organization_id: config.organization_id },
  }));
  const receive = (event) => {
    const data = event.data;
    if (event.source !== window.parent || event.origin !== origin || !data
      || data.type !== 'mib:website-consent' || data.version !== 1 || data.channel !== channel
      || typeof data.analytics !== 'boolean' || typeof data.marketing !== 'boolean') return;
    if (connection.choice?.analytics === data.analytics && connection.choice?.marketing === data.marketing) return;
    connection.choice = {
      version: VERSION, revision: config.revision, source: 'website_complianz',
      analytics: data.analytics, marketing: data.marketing, saved_at: new Date().toISOString(),
    };
    changed();
  };
  window.addEventListener('message', receive);
  window.parent.postMessage({ type: 'mib:consent-request', version: 1, channel }, origin);
  return () => {
    window.removeEventListener('message', receive);
    if (embeddedConsent === connection) {
      embeddedConsent = null;
      changed();
    }
  };
}
